import React, { useState, useEffect, useContext, useCallback } from 'react';
import Chart from 'react-apexcharts';
import {
  Calendar,
  Filter,
  TrendingUp,
  Package,
  Clock,
  UserCheck,
  Activity
} from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import { ThemeContext } from '../context/ThemeContext';
import PageHeader from '../components/layout/PageHeader';
import GlassmorphicCard from '../components/common/GlassmorphicCard';
import InputField from '../components/common/InputField';
import Button from '../components/common/Button';
import Table from '../components/common/Table';
import { fetchData } from '../api/api';
import { formatCurrency } from '../utils/currency';
import { formatDate } from '../utils/date';
import useDebounce from '../utils/debounce';

const reportTypes = [
  { value: 'sales', label: 'Sales & Revenue Overview', icon: TrendingUp },
  { value: 'products', label: 'Product Sales Performance', icon: Package },
  { value: 'expiry', label: 'Product Expiry Tracker', icon: Clock },
  { value: 'employees', label: 'Cashier & Staff Performance', icon: UserCheck },
  { value: 'inventories', label: 'Stock Movement Dynamics', icon: Activity },
];

const ReportsPage = () => {
  const { authToken, selectedShopCode } = useContext(AuthContext);
  const { theme } = useContext(ThemeContext);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportType, setReportType] = useState('sales');
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isDark = theme === 'dark';

  const debouncedStartDate = useDebounce(startDate, 600);
  const debouncedEndDate = useDebounce(endDate, 600);
  const debouncedReportType = useDebounce(reportType, 600);

  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
  }, []);

  const fetchReportData = useCallback(async () => {
    if (!authToken || !selectedShopCode) return;
    setLoading(true);
    setError('');
    try {
      let endpoint = '';
      switch (debouncedReportType) {
        case 'sales':
          endpoint = `api/pos/v1/report/sales/days?date_start=${debouncedStartDate}&date_end=${debouncedEndDate}`;
          break;
        case 'products':
          endpoint = `api/pos/v1/report/products/days?date_start=${debouncedStartDate}&date_end=${debouncedEndDate}`;
          break;
        case 'expiry':
          endpoint = `api/pos/v1/report/products/expiry`;
          break;
        case 'employees':
          endpoint = `api/pos/v1/report/employees/days?date_start=${debouncedStartDate}&date_end=${debouncedEndDate}`;
          break;
        case 'inventories':
          endpoint = `api/pos/v1/report/inventories/days?date_start=${debouncedStartDate}&date_end=${debouncedEndDate}`;
          break;
        default:
          break;
      }

      if (endpoint) {
        const data = await fetchData(endpoint, 'GET', null, authToken, selectedShopCode);
        setReportData(data.data || []);
      }
    } catch (err) {
      setError(`Failed to generate ${debouncedReportType} report.`);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  }, [authToken, selectedShopCode, debouncedReportType, debouncedStartDate, debouncedEndDate]);

  useEffect(() => {
    if (debouncedStartDate && debouncedEndDate) {
      fetchReportData();
    }
  }, [fetchReportData, debouncedStartDate, debouncedEndDate]);

  // Headers for each report table
  const getHeaders = () => {
    switch (debouncedReportType) {
      case 'sales':
        return ['Period Date', 'Sales Revenue', 'Order Volume', 'Discounts', 'Net Profit'];
      case 'products':
        return ['Product Name', 'Units Sold', 'Revenue Generated', 'Cost Value', 'Gross Profit'];
      case 'expiry':
        return ['Product Item', 'Expiration Date', 'Units in Stock'];
      case 'employees':
        return ['Cashier Staff', 'Total Sales Processed', 'Orders Fulfilled'];
      case 'inventories':
        return ['Product', 'Stock Delta', 'Timestamp'];
      default:
        return [];
    }
  };

  // ApexCharts Config & Series
  const getChartConfig = () => {
    if (!reportData || reportData.length === 0) return null;

    const baseOptions = {
      chart: {
        fontFamily: 'Lato, sans-serif',
        toolbar: { show: false },
        background: 'transparent',
      },
      grid: {
        borderColor: isDark ? '#334155' : '#e2e8f0',
        strokeDashArray: 4,
      },
      tooltip: { theme: isDark ? 'dark' : 'light' },
    };

    if (debouncedReportType === 'sales') {
      return {
        type: 'area',
        series: [
          { name: 'Sales Revenue', data: reportData.map(d => +d.saleValue || 0) },
          { name: 'Gross Profit', data: reportData.map(d => +d.pnlValue || 0) },
        ],
        options: {
          ...baseOptions,
          colors: ['#059669', '#0d9488'],
          stroke: { curve: 'smooth', width: 2.5 },
          fill: { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0.05 } },
          xaxis: {
            categories: reportData.map(d => d.period || ''),
            labels: { style: { colors: isDark ? '#94a3b8' : '#64748b' } },
          },
          yaxis: {
            labels: {
              style: { colors: isDark ? '#94a3b8' : '#64748b' },
              formatter: (val) => formatCurrency(val),
            },
          },
        },
      };
    }

    if (debouncedReportType === 'products') {
      const top10 = reportData.slice(0, 10);
      return {
        type: 'bar',
        series: [
          { name: 'Revenue', data: top10.map(d => +d.sellingValue || 0) },
          { name: 'Profit', data: top10.map(d => +d.pnlValue || 0) },
        ],
        options: {
          ...baseOptions,
          plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
          colors: ['#059669', '#14b8a6'],
          xaxis: {
            categories: top10.map(d => d.name || ''),
            labels: { style: { colors: isDark ? '#94a3b8' : '#64748b' }, trim: true },
          },
          yaxis: {
            labels: {
              style: { colors: isDark ? '#94a3b8' : '#64748b' },
              formatter: (val) => formatCurrency(val),
            },
          },
        },
      };
    }

    if (debouncedReportType === 'employees') {
      return {
        type: 'bar',
        series: [
          { name: 'Sales Processed', data: reportData.map(d => +d.sellingValue || 0) },
        ],
        options: {
          ...baseOptions,
          plotOptions: { bar: { borderRadius: 6, columnWidth: '40%' } },
          colors: ['#0ea5e9'],
          xaxis: {
            categories: reportData.map(d => d.name || ''),
            labels: { style: { colors: isDark ? '#94a3b8' : '#64748b' } },
          },
          yaxis: {
            labels: {
              style: { colors: isDark ? '#94a3b8' : '#64748b' },
              formatter: (val) => formatCurrency(val),
            },
          },
        },
      };
    }

    if (debouncedReportType === 'inventories') {
      return {
        type: 'line',
        series: [
          { name: 'Stock Movement', data: reportData.map(d => +d.stockChange || 0) },
        ],
        options: {
          ...baseOptions,
          stroke: { curve: 'straight', width: 2 },
          colors: ['#8b5cf6'],
          xaxis: {
            categories: reportData.map(d => d.date || ''),
            labels: { style: { colors: isDark ? '#94a3b8' : '#64748b' } },
          },
        },
      };
    }

    if (debouncedReportType === 'expiry') {
      const top10 = reportData.slice(0, 10);
      return {
        type: 'bar',
        series: [
          { name: 'Units Expiring', data: top10.map(d => +d.quantity || 0) },
        ],
        options: {
          ...baseOptions,
          plotOptions: { bar: { borderRadius: 4, columnWidth: '40%' } },
          colors: ['#f59e0b'],
          xaxis: {
            categories: top10.map(d => d.name || ''),
            labels: { style: { colors: isDark ? '#94a3b8' : '#64748b' } },
          },
        },
      };
    }

    return null;
  };

  const chart = getChartConfig();

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reports & Business Intelligence"
        subtitle="Analyze sales velocity, profit margins, cashier performance, and inventory trends"
        breadcrumbs={[{ label: 'Reports' }]}
      />

      {/* Report Type Selector Pills */}
      <div className="flex flex-wrap gap-2">
        {reportTypes.map((t) => {
          const Icon = t.icon;
          const isActive = reportType === t.value;
          return (
            <button
              key={t.value}
              onClick={() => setReportType(t.value)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                isActive
                  ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/25 ring-2 ring-emerald-600/30'
                  : 'bg-white dark:bg-[#1e293b] text-slate-600 dark:text-slate-300 border border-slate-200/80 dark:border-slate-700 hover:bg-slate-50'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Date Range Selector */}
      {reportType !== 'expiry' && (
        <GlassmorphicCard noPadding>
          <div className="p-4 md:p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
              <div className="w-full sm:w-48">
                <InputField
                  label="From Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  icon={<Calendar className="w-4 h-4" />}
                  className="!mb-0"
                />
              </div>
              <div className="w-full sm:w-48">
                <InputField
                  label="To Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  icon={<Calendar className="w-4 h-4" />}
                  className="!mb-0"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 self-end sm:self-auto">
              <Button
                onClick={() => {
                  const today = new Date();
                  const d = new Date(today);
                  d.setDate(today.getDate() - 7);
                  setEndDate(today.toISOString().split('T')[0]);
                  setStartDate(d.toISOString().split('T')[0]);
                }}
                variant="light"
                size="sm"
              >
                7 Days
              </Button>
              <Button
                onClick={() => {
                  const today = new Date();
                  const d = new Date(today);
                  d.setDate(today.getDate() - 30);
                  setEndDate(today.toISOString().split('T')[0]);
                  setStartDate(d.toISOString().split('T')[0]);
                }}
                variant="light"
                size="sm"
              >
                30 Days
              </Button>
              <Button
                onClick={fetchReportData}
                icon={<Filter className="w-3.5 h-3.5" />}
                size="sm"
              >
                Generate
              </Button>
            </div>
          </div>
        </GlassmorphicCard>
      )}

      {/* Chart Visualization Card */}
      {chart && (
        <GlassmorphicCard
          title="Interactive Analytics Visualization"
          subtitle={`Graphical metrics for ${reportTypes.find(r => r.value === reportType)?.label}`}
          noPadding
        >
          <div className="p-4 md:p-6">
            {loading ? (
              <div className="h-[350px] flex items-center justify-center text-slate-400">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                  <span>Generating analytics charts...</span>
                </div>
              </div>
            ) : (
              <Chart
                options={chart.options}
                series={chart.series}
                type={chart.type}
                height={350}
              />
            )}
          </div>
        </GlassmorphicCard>
      )}

      {/* Data Table Breakdown */}
      <GlassmorphicCard
        title="Detailed Record Breakdown"
        subtitle={`Tabular export records for ${reportTypes.find(r => r.value === reportType)?.label}`}
        noPadding
      >
        <Table
          headers={getHeaders()}
          data={reportData}
          loading={loading}
          emptyMessage={error || 'No data records found for the selected parameters.'}
          renderRow={(item, idx) => {
            if (reportType === 'sales') {
              return (
                <tr key={idx} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                  <td className="px-6 py-3.5 font-semibold text-slate-900 dark:text-white">{item.period}</td>
                  <td className="px-6 py-3.5 font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(item.saleValue)}</td>
                  <td className="px-6 py-3.5">{item.count} orders</td>
                  <td className="px-6 py-3.5 text-slate-400">{formatCurrency(item.discount)}</td>
                  <td className="px-6 py-3.5 font-semibold text-teal-600 dark:text-teal-400">{formatCurrency(item.pnlValue)}</td>
                </tr>
              );
            }
            if (reportType === 'products') {
              return (
                <tr key={idx} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                  <td className="px-6 py-3.5 font-semibold text-slate-900 dark:text-white">{item.name}</td>
                  <td className="px-6 py-3.5 font-bold">{item.quantity}</td>
                  <td className="px-6 py-3.5 font-bold text-emerald-600">{formatCurrency(item.sellingValue)}</td>
                  <td className="px-6 py-3.5 text-slate-400">{formatCurrency(item.costValue)}</td>
                  <td className="px-6 py-3.5 font-semibold text-teal-600">{formatCurrency(item.pnlValue)}</td>
                </tr>
              );
            }
            if (reportType === 'expiry') {
              return (
                <tr key={idx} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                  <td className="px-6 py-3.5 font-semibold text-slate-900 dark:text-white">{item.name}</td>
                  <td className="px-6 py-3.5 font-mono text-amber-600">{formatDate(item.expiry)}</td>
                  <td className="px-6 py-3.5 font-bold text-slate-700 dark:text-slate-300">{item.quantity} left</td>
                </tr>
              );
            }
            if (reportType === 'employees') {
              return (
                <tr key={idx} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                  <td className="px-6 py-3.5 font-semibold text-slate-900 dark:text-white">{item.name}</td>
                  <td className="px-6 py-3.5 font-bold text-emerald-600">{formatCurrency(item.sellingValue)}</td>
                  <td className="px-6 py-3.5">{item.count} transactions</td>
                </tr>
              );
            }
            if (reportType === 'inventories') {
              return (
                <tr key={idx} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                  <td className="px-6 py-3.5 font-semibold text-slate-900 dark:text-white">{item.name}</td>
                  <td className="px-6 py-3.5 font-bold">{item.quantity}</td>
                  <td className="px-6 py-3.5 text-slate-500">{formatDate(item.date)}</td>
                </tr>
              );
            }
            return null;
          }}
        />
      </GlassmorphicCard>
    </div>
  );
};

export default ReportsPage;
